import { supabase } from '../supabase';
import { config } from '../config';
import type { ProfileInsert, Review, ConsentLog } from '../types';

/**
 * Get redirect URL for authentication
 * IMPORTANT: Don't include hash fragment - Supabase strips it
 * Instead, redirect to base URL and let our route detection handle it
 */
const getAuthRedirectUrl = () => {
  const origin = window.location.origin;
  
  // Redirect to base URL only (no hash fragment)
  // Supabase will append tokens like: https://cannabias.co.uk/#access_token=xxx
  // Our route detection will then catch the tokens and route to /verify
  const redirectUrl = origin;
  
  console.log('🔗 Magic link redirect URL:', redirectUrl);
  console.log('ℹ️  Note: Supabase will append auth tokens to this URL');
  
  return redirectUrl;
};

/**
 * Sign up with email (magic link)
 */
export const signUpWithEmail = async (email: string, displayName?: string) => {
  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: getAuthRedirectUrl(),
      data: {
        display_name: displayName,
      },
    },
  });

  if (error) throw error;
  return data;
};

/**
 * Sign in with email (magic link)
 */
export const signInWithEmail = async (email: string) => {
  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: getAuthRedirectUrl(),
    },
  });

  if (error) throw error;
  return data;
};

/**
 * Sign in with Discord (OAuth)
 */
export const signInWithDiscord = async () => {
  const redirectUrl = getAuthRedirectUrl();

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'discord',
    options: {
      redirectTo: redirectUrl,
    },
  });

  if (error) throw error;
  return data;
};

/**
 * Verify OTP from email
 */
export const verifyOtp = async (email: string, token: string) => {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  });

  if (error) throw error;
  return data;
};

/**
 * Sign out
 */
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

/**
 * Get current session
 */
export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

/**
 * Get current user
 */
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

/**
 * Create user profile after signup
 * This should be called after email verification
 */
export const createProfile = async (userId: string, data: Partial<ProfileInsert>) => {
  const { data: profile, error } = await supabase
    .from('profiles')
    .insert({
      id: userId,
      display_name: data.display_name,
      role: data.role || 'patient',
      clinic_id: data.clinic_id,
      pharmacy_id: data.pharmacy_id,
    })
    .select()
    .single();

  if (error) throw error;
  return profile;
};

/**
 * Update user profile
 */
export const updateProfile = async (userId: string, data: Partial<ProfileInsert>) => {
  const { error } = await supabase
    .from('profiles')
    .update(data)
    .eq('id', userId);

  if (error) throw error;
  
  // Fetch the updated profile
  const { data: profile, error: fetchError } = await supabase
    .from('profiles')
    .select()
    .eq('id', userId)
    .single();
  
  if (fetchError) throw fetchError;
  return profile;
};

/**
 * Get user profile with consent status
 */
export const getProfile = async (userId: string) => {
  // 🔧 DEVELOPMENT MODE - Return mock profile with consent
  if (config.devMode.enabled && config.devMode.mockAuth) {
    // Check if consent has been given this session (stored in sessionStorage)
    const hasConsented = sessionStorage.getItem(`consent_given_${userId}`) === 'true';
    
    console.log('🔧 DEV MODE: getProfile returning mock profile, hasConsented:', hasConsented);
    
    return {
      id: userId,
      display_name: 'Test Patient',
      email: 'patient@cannabias.co.uk',
      role: 'patient',
      clinic_id: null,
      pharmacy_id: null,
      team_role: null,
      last_sign_in_at: new Date().toISOString(),
      is_verified_patient: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      consented_at: hasConsented ? new Date().toISOString() : null,
    };
  }
  
  // Fetch profile and latest consent in parallel
  const [profileResult, consentResult] = await Promise.all([
    supabase
      .from('profiles')
      .select('*, team_role, email, last_sign_in_at, clinic:clinics(*), pharmacy:pharmacies(*)')
      .eq('id', userId)
      .single(),
    supabase
      .from('consent_logs')
      .select('created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
  ]);

  if (profileResult.error) throw profileResult.error;
  
  // Add consented_at from consent_logs to the profile
  const profile = {
    ...profileResult.data,
    consented_at: consentResult.data?.created_at || null
  };
  
  return profile;
};

/**
 * Check if user has completed onboarding (has profile + consent)
 */
export const hasCompletedOnboarding = async (userId: string) => {
  // Check if profile exists
  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', userId)
    .single();

  if (!profile) return false;

  // Check if consent exists
  const { data: consent } = await supabase
    .from('consent_logs')
    .select('id')
    .eq('user_id', userId)
    .limit(1)
    .single();

  return !!consent;
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  return supabase.auth.onAuthStateChange(callback);
};

/**
 * Export user data (GDPR compliance)
 */
export const exportUserData = async (userId: string): Promise<any> => {
  // 🔧 DEVELOPMENT MODE - Return mock data
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Exporting mock user data');
    return {
      profile: {
        id: userId,
        email: 'demo@cannabias.tech',
        display_name: 'Demo Patient',
        role: 'patient',
        created_at: new Date().toISOString(),
      },
      reviews: [
        {
          id: 'review-1',
          product_name: 'Blue Dream',
          stars: 5,
          submitted_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'review-2',
          product_name: 'Northern Lights',
          stars: 4,
          submitted_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        },
      ],
      consent_logs: [
        {
          id: 'consent-1',
          consent_type: 'terms',
          consented: true,
          created_at: new Date().toISOString(),
        },
      ],
      exported_at: new Date().toISOString(),
    };
  }
  
  // Get all user data
  const [profile, reviews, consentLogs] = await Promise.all([
    supabase.from('profiles').select('*').eq('id', userId).single(),
    supabase.from('reviews').select('*').eq('author_id', userId),
    supabase.from('consent_logs').select('*').eq('user_id', userId),
  ]);

  return {
    profile: profile.data,
    reviews: reviews.data || [],
    consent_logs: consentLogs.data || [],
    exported_at: new Date().toISOString(),
  };
};

/**
 * Delete user account (GDPR compliance)
 * Anonymizes reviews but keeps them in the system
 */
export const deleteUserAccount = async (userId: string): Promise<void> => {
  // First, anonymize all reviews
  await supabase
    .from('reviews')
    .update({ author_id: null })
    .eq('author_id', userId);

  // Delete consent logs
  await supabase
    .from('consent_logs')
    .delete()
    .eq('user_id', userId);

  // Delete profile (this will cascade to auth.users via RLS)
  const { error } = await supabase
    .from('profiles')
    .delete()
    .eq('id', userId);

  if (error) throw error;
};